import apiService from 'frontcore/data/api-service/api-service'
import core from 'frontcore/index'
import i18n from 'frontcore/i18n'
import useLoginConfigurationStore from 'frontcore/domain/authorization/store/useLoginConfigurationStore'
import { defineStore } from 'pinia'
import { LoginSettingsAPIResponse } from 'frontcore/domain/authorization/model/types'
import { PROMISE_STATUS } from 'frontcore/constants/promises'
import { readonly, ref, Ref } from 'vue'

export class ConfigurationBaseError extends Error {}

type ConfigurationBaseAPIResponse = {
  AVAILABLE_LANGUAGES: string[]
  COMPANY_NAME: string
  FACTORY_NAME: string
  FACTORY_TIME_ZONE: string
  LOGIN_TYPE: LoginSettingsAPIResponse
}

interface ConfigurationBaseStore {
  companyName: Ref<string | null>
  factoryName: Ref<string | null>
  timeZone: Ref<string>
  availableLanguages: Ref<string[]>
  loginType: Ref<LoginSettingsAPIResponse | null>
  fetchConfigurationBase: () => Promise<void>
}

const useConfigurationBaseStore = defineStore('configurationBase', (): ConfigurationBaseStore => {
  const status = ref(PROMISE_STATUS.notStarted)
  const timeZone = ref('Europe/Madrid')
  const factoryName = ref<string | null>(null)
  const companyName = ref<string | null>(null)
  const loginType = ref<LoginSettingsAPIResponse | null>(null)
  const availableLanguages = ref<string[]>([])

  const { createOpenIDConfiguration, setLoginConfiguration } = useLoginConfigurationStore()

  const { t } = i18n.global
  
  const fetchConfigurationBase = async () => {
    try {
      if (status.value === PROMISE_STATUS.notStarted) {
        status.value = PROMISE_STATUS.pending
        const { data: configuration } = await apiService
          .buildRequest()
          .withSessionToken()
          .get<ConfigurationBaseAPIResponse>('configurations/base/')
        
        const publicPath = typeof ENV_PUBLIC_PATH === 'undefined' ? undefined : ENV_PUBLIC_PATH
        setLoginConfiguration(configuration.LOGIN_TYPE)
        createOpenIDConfiguration(API_URL, publicPath)
        core.setFactoryTimezone(configuration.FACTORY_TIME_ZONE)
        factoryName.value = configuration.FACTORY_NAME
        timeZone.value = configuration.FACTORY_TIME_ZONE
        companyName.value = configuration.COMPANY_NAME
        availableLanguages.value = configuration.AVAILABLE_LANGUAGES
        loginType.value = configuration.LOGIN_TYPE
        status.value = PROMISE_STATUS.complete
      }
    } catch {
      status.value = PROMISE_STATUS.notStarted
      throw new ConfigurationBaseError(t('login.errors.missing_configuration'))
    }
  }

  return {
    companyName: readonly(companyName),
    factoryName: readonly(factoryName),
    timeZone: readonly(timeZone),
    availableLanguages: availableLanguages,
    loginType: readonly(loginType),
    fetchConfigurationBase,
  }
})

export default useConfigurationBaseStore
